import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { theEditionService, TheEditionResponse, Post } from "../services/api";
import { SectionHeader } from "./SectionHeader";
import { Star, Eye, MessageCircle, ChevronDown, ChevronUp } from "lucide-react";

export const TheEdition = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [newCount, setNewCount] = useState<number>(0);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  const [isXS, setIsXS] = useState(false);

  const togglePost = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  useEffect(() => {
    const saved = localStorage.getItem("edition_lang");
    if (saved) setSelectedLanguage(saved);
  }, []);

  const truncateContent = (content: string, maxLength: number = 150): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + "...";
  };

  useEffect(() => {
    const fetchEdition = async () => {
      try {
        setLoading(true);
        const langs = selectedLanguage ? [selectedLanguage] : undefined;
        const data: TheEditionResponse = await theEditionService.getEdition(langs);
        setPosts(data.posts);
        setNewCount(data.new_articles_count);
        setMessage(data.message ?? null);
      } catch (error) {
        console.error("Error fetching The Edition:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEdition();
  }, [selectedLanguage]);

  useEffect(() => {
    const update = () => {
      if (typeof window !== "undefined") {
        const w = window.innerWidth;
        setIsXS(w < 360);
        setIsMobile(w < 640);
        setIsMedium(w >= 640 && w < 1024);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (posts.length > 0 && typeof window !== "undefined" && window.innerWidth >= 1280) {
      const allExpanded: Record<string, boolean> = {};
      posts.forEach((p) => {
        allExpanded[p.id] = true;
      });
      setExpanded(allExpanded);
    }
  }, [posts]);

  useEffect(() => {
    localStorage.setItem("edition_lang", selectedLanguage);
  }, [selectedLanguage]);

  if (loading)
    return (
      <div
        className="the-edition-container w-full flex flex-row"
        style={{
          // paddingTop: "47px",
          paddingBottom: "47px",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "30px",
          margin: "0 auto",
        }}
      >
        <div
          className="the-edition-header"
          style={{
            width: "100%",
            background: "rgba(0, 0, 0, 0)",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "17px",
            display: "flex",
            paddingTop: "17px",
            paddingBottom: "17px",
          }}
        >
          {/* <div
            style={{
              alignSelf: "center",
              height: "44px",
              color: "#262626",
              fontSize: "36px",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            The Edition {newCount > 0 && <span>({newCount} new posts)</span>}
          </div> */}
          <p
            style={{
              color: "#525252",
              fontSize: "18px",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
              textAlign: "center",
            }}
          >
            The Edition is your personal issue of Beatpost, articles from the Voices you follow, gathered here as a
            curated daily read.
          </p>
          {/* Separador horizontal */}
          <div
            className="hidden lg:block"
            style={{
              width: "100%",
              height: "1px",
              backgroundColor: "#D3D3D3",
            }}
          />
        </div>
        <div className="articles-list-loading space-y-6 px-11 w-full">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="beat-card p-6">
              <div className="beat-loading h-6 mb-4"></div>
              <div className="beat-loading h-4 mb-2"></div>
              <div className="beat-loading h-32"></div>
            </div>
          ))}
        </div>
      </div>
    );
  if (message) return <div>{message}</div>;

  return (
    <div
      className="the-edition-container w-full flex flex-row"
      style={{
        // paddingTop: "47px",
        paddingBottom: "47px",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "30px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        className="the-edition-header"
        style={{
          width: "100%",
          background: "rgba(0, 0, 0, 0)",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: isMobile ? "12px" : isMedium ? "14px" : "17px",
          display: "flex",
          paddingTop: isXS ? "12px" : isMobile ? "14px" : "17px",
          paddingBottom: isXS ? "12px" : isMobile ? "14px" : "17px",
        }}
      >
        {/* <div
          // className="text-center text-4xl font-playfair font-semibold mb-2"
          style={{
            alignSelf: "center",
            height: "44px",
            color: "#262626",
            fontSize: "36px",
            fontFamily: "Abhaya Libre",
            fontWeight: "400",
            wordWrap: "break-word",
          }}
        >
          The Edition {newCount > 0 && <span>({newCount} new posts)</span>}
        </div> */}
        <p
          style={{
            color: "#525252",
            fontSize: isXS ? "16px" : isMobile ? "17px" : isMedium ? "17px" : "18px",
            fontFamily: "Instrument Sans",
            fontWeight: "400",
            lineHeight: isXS ? "24px" : isMobile ? "26px" : "28px",
            wordWrap: "break-word",
            textAlign: "center",
          }}
        >
          The Edition is your personal issue of Beatpost, articles from the Voices you follow, gathered here as a
          curated daily read.
        </p>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: isMobile || isMedium ? "center" : "flex-end",
            paddingLeft: isMobile ? "8px" : "11px",
            paddingRight: isMobile ? "8px" : "11px",
            gap: isMobile ? "8px" : "10px",
            flexWrap: isMobile || isMedium ? "wrap" : "nowrap",
          }}
        >
          <label
            htmlFor="edition-language"
            style={{
              marginRight: isMobile || isMedium ? "6px" : "8px",
              color: "#525252",
              fontSize: isMobile ? "13px" : "14px",
              fontFamily: "Instrument Sans",
            }}
          >
            Language:
          </label>
          <select
            id="edition-language"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            style={{
              padding: isMobile ? "6px 10px" : "6px",
              borderRadius: "6px",
              border: "1px solid #D3D3D3",
              color: "#262626",
              fontSize: isMobile ? "14px" : "14px",
            }}
          >
            <option value="">All</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="pt">Português</option>
            <option value="it">Italiano</option>
            <option value="ru">Русский</option>
            <option value="tr">Türkçe</option>
            <option value="pl">Polski</option>
            <option value="uk">Українська</option>
            <option value="nl">Nederlands</option>
            <option value="ro">Română</option>
            <option value="el">Ελληνικά</option>
            <option value="sv">Svenska</option>
            <option value="cs">Čeština</option>
            <option value="hu">Magyar</option>
            <option value="sh">Srpskohrvatski</option>
            <option value="bg">Български</option>
            <option value="fi">Suomi</option>
            <option value="da">Dansk</option>
            <option value="sk">Slovenčina</option>
            <option value="no">Norsk</option>
          </select>
        </div>
        {/* Separador horizontal */}
        <div
          className="hidden lg:block"
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: "#D3D3D3",
          }}
        />
      </div>

      {posts.length === 0 ? (
        <div
          className="the-edition-articles-list space-y-5"
          style={{
            paddingBottom: "60px",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          <p>There are no articles yet.</p>
        </div>
      ) : (
        <div
          className="the-edition-articles-list space-y-5"
          style={{
            paddingBottom: "60px",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          {posts.map((post, index) => (
            <div
              className="the-edition-article flex-wrap sm:flex-nowrap"
              key={post.id}
              data-property-1="Variant3"
              style={{
                width: "100%",
                background: "rgba(0, 0, 0, 0)",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: typeof window !== "undefined" && window.innerWidth < 640 && !expanded[post.id] ? "12px" : "28px",
                display: "inline-flex",
                overflow: "hidden",
                marginBottom:
                  typeof window !== "undefined" && window.innerWidth < 640 && !expanded[post.id] ? "8px" : "24px",
              }}
              onClick={() => {
                if (typeof window !== "undefined" && window.innerWidth < 640) {
                  if (!expanded[post.id]) {
                    setExpanded((prev) => ({ ...prev, [post.id]: true }));
                    return;
                  }
                  navigate(`/post/${post.id}`);
                }
              }}
            >
              {expanded[post.id] && (
                <img
                  className="the-edition-article-image"
                  style={{
                    width: "143px",
                    height: "143px",
                    background: "#D9D9D9",
                    objectFit: "cover",
                  }}
                  src={post.image || "/Beatpost_defaut_image.png"}
                  alt=""
                />
              )}
              <div
                className="the-edition-article-content"
                style={{
                  width: "100%",
                  background: "rgba(0, 0, 0, 0)",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: "9px",
                  display: "inline-flex",
                }}
              >
                <div
                  style={{
                    alignSelf: "stretch",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Link
                    className="the-edition-article-title"
                    to={`/post/${post.id}`}
                    style={{ textDecoration: "none", flex: 1 }}
                    onClick={(e) => {
                      if (typeof window !== "undefined" && window.innerWidth < 640) {
                        if (!expanded[post.id]) {
                          e.preventDefault();
                          setExpanded((prev) => ({ ...prev, [post.id]: true }));
                        }
                      }
                    }}
                  >
                    <div
                      style={{
                        alignSelf: "stretch",
                        color: "#262626",
                        fontSize: "20px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "700",
                        lineHeight: "25px",
                        wordWrap: "break-word",
                      }}
                    >
                      {post.title}
                    </div>
                  </Link>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePost(post.id);
                    }}
                    className="xl:hidden"
                    style={{
                      background: "transparent",
                      border: "none",
                      paddingLeft: "8px",
                      paddingRight: "8px",
                      cursor: "pointer",
                    }}
                    aria-expanded={!!expanded[post.id]}
                  >
                    {expanded[post.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>
                {expanded[post.id] && (
                  <div
                    className="the-edition-article-excerpt"
                    style={{
                      alignSelf: "stretch",
                      color: "#525252",
                      fontSize: "14px",
                      fontFamily: "Instrument Sans",
                      fontWeight: "400",
                      lineHeight: "20px",
                      wordWrap: "break-word",
                    }}
                  >
                    {truncateContent(post.content)}
                  </div>
                )}
                {expanded[post.id] && (
                  <div
                    className="the-edition-article-meta"
                    style={{
                      width: "100%",
                      gap: "1rem",
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      display: "inline-flex",
                    }}
                  >
                    <div
                      className="the-edition-article-meta-author"
                      style={{
                        width: "max-content",
                        height: "16px",
                        color: "#737373",
                        fontSize: "13px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "400",
                        lineHeight: "16px",
                        letterSpacing: "0.6px",
                        wordWrap: "break-word",
                      }}
                    >
                      By {post.author_username}
                    </div>

                    {post.hashtags.slice(0, 3).map((tag, tagIndex) => (
                      <div
                        key={tag}
                        style={{
                          width: tagIndex === 0 ? "max-content" : "max-content",
                          height: "16px",
                          color: "#A3A3A3",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre",
                          fontWeight: "400",
                          lineHeight: "16px",
                          wordWrap: "break-word",
                          cursor: "pointer",
                        }}
                      >
                        #{tag}
                      </div>
                    ))}
                  </div>
                )}
                {expanded[post.id] && (
                  <div
                    style={{
                      alignSelf: "stretch",
                      background: "rgba(0, 0, 0, 0)",
                      justifyContent: "space-between",
                      alignItems: "center",
                      display: "inline-flex",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <Star className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          textAlign: "center",
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          lineHeight: "16px",
                          letterSpacing: "0.6px",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.average_rating.toFixed(1)}
                      </div>
                    </div>
                    <div
                      style={{
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <Eye className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.visits.toLocaleString()} visits
                      </div>
                    </div>
                    <div
                      style={{
                        width: "98.12px",
                        alignSelf: "stretch",
                        background: "rgba(0, 0, 0, 0)",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        gap: "8px",
                        display: "flex",
                      }}
                    >
                      <MessageCircle className="w-4 h-4" style={{ color: "#737373" }} />

                      <div
                        style={{
                          color: "#737373",
                          fontSize: "13px",
                          fontFamily: "Abhaya Libre ExtraBold",
                          fontWeight: "800",
                          wordWrap: "break-word",
                        }}
                      >
                        {post.comments_count} comments
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
